/**
 * Dois cenários lado a lado.
 *
 * **O placar não decide nada.** "Cenário A: 4 aulas sem lugar; cenário B: 4
 * aulas sem lugar" não ajuda ninguém a escolher — as duas grades podem ser
 * completamente diferentes com o mesmo número. O que separa uma da outra é
 * ONDE cada uma pagou: uma tem janela de professor, a outra tem a disciplina
 * pesada no último horário. É isso que a coordenação compara quando compara,
 * e é por isso que o corpo da tela é a lista de critérios, não o resumo.
 *
 * **Só aparece o que difere, e o que difere aparece marcado.** Trinta e dois
 * critérios idênticos nas duas colunas são trinta e duas linhas de ruído
 * escondendo as três que importam. Há um interruptor para ver tudo, porque
 * "por que este critério não aparece?" é uma pergunta legítima.
 *
 * O melhor valor de cada linha fica em negrito, e não colorido: verde e
 * vermelho aqui diriam "certo" e "errado", e menos janela não é certo — é uma
 * escolha que custa outra coisa em outra linha.
 */

import { useEffect, useMemo, useState } from 'react'

import { ErroApi, horario, type Cenario } from '../api'
import { criterioLegivel, maisBarata } from '../comparacao'
import { Icon } from '../components/Icon'

interface Coluna {
  id: string
  nome: string
  processado_em: string | null
  status_solucao: string | null
  conclusao: string
  aulas_alocadas: number
  aulas_pendentes: number
  valor_objetivo: number
  tempo_solve_s: number
  relaxacoes: number
  /** Reais/mês da grade, em centavos. Nulo sem grade: não há o que comparar. */
  custo_mes: number | null
  janela_mes: number | null
  custo_aulas_pendentes: number | null
}

/** Centavos inteiros → `R$ 1.234,56`. */
const reais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })


interface Linha {
  criterio_id: string
  valores: { cenario_id: string; ocorrencias: number; custo: number }[]
  difere: boolean
}

/** `professor_janela` → `Professor janela`. O id é do motor, não da pessoa. */
const legivel = criterioLegivel

export function Comparar({ projeto }: { projeto: string }) {
  const [cenarios, setCenarios] = useState<Cenario[]>([])
  const [escolhidos, setEscolhidos] = useState<string[]>([])
  const [dados, setDados] = useState<{ colunas: Coluna[]; linhas: Linha[] } | null>(null)
  const [tudo, setTudo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    horario
      .cenarios(projeto)
      .then((itens) => {
        setCenarios(itens)
        setEscolhidos(itens.slice(0, 2).map((c) => c.id))
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar'))
  }, [projeto])

  useEffect(() => {
    if (escolhidos.length < 2) {
      setDados(null)
      return
    }
    horario
      .comparar(projeto, escolhidos)
      .then((r) => {
        setDados(r)
        setErro(null)
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível comparar'))
  }, [projeto, escolhidos])

  const linhas = useMemo(
    () => (dados?.linhas ?? []).filter((l) => tudo || l.difere),
    [dados, tudo],
  )

  function alternar(id: string) {
    setEscolhidos((atuais) =>
      atuais.includes(id) ? atuais.filter((x) => x !== id) : [...atuais, id],
    )
  }

  return (
    <>
      <header className="page-head">
        <h1>Comparar cenários</h1>
        <p className="page-head__note">
          {escolhidos.length < 2
            ? 'Escolha ao menos dois.'
            : `${escolhidos.length} cenários, critério por critério`}
        </p>
      </header>

      <div className="grade__controles no-print">
        {cenarios.map((c) => (
          <label key={c.id} className="chip">
            <input
              type="checkbox"
              checked={escolhidos.includes(c.id)}
              onChange={() => alternar(c.id)}
            />
            {c.nome}
          </label>
        ))}
      </div>

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      {cenarios.length < 2 && !erro && (
        <div className="empty">
          <p className="empty__title">Só há um cenário neste projeto.</p>
          <p className="empty__body">
            Um cenário é uma tentativa: mesmos dados, pesos diferentes. Comparar
            duas tentativas é o que diz qual delas a escola prefere — e é o que o
            URÂNIA não faz.
          </p>
        </div>
      )}

      {dados && (
        <>
          <table className="comparar">
            <thead>
              <tr>
                <th scope="col">&nbsp;</th>
                {dados.colunas.map((col) => (
                  <th scope="col" key={col.id}>
                    {col.nome}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Resumo colunas={dados.colunas} />
            </tbody>
          </table>

          <div className="section__head">
            <h2>Onde cada um pagou</h2>
            <label className="field__hint">
              <input type="checkbox" checked={tudo} onChange={(e) => setTudo(e.target.checked)} />{' '}
              mostrar também os critérios iguais nos dois
            </label>
          </div>

          {linhas.length === 0 ? (
            <p className="field__hint">
              {dados.linhas.length === 0
                ? 'Nenhum dos cenários foi processado ainda.'
                : 'Os cenários pagaram exatamente o mesmo em todos os critérios.'}
            </p>
          ) : (
            <table className="comparar">
              <thead>
                <tr>
                  <th scope="col">Critério</th>
                  {dados.colunas.map((col) => (
                    <th scope="col" key={col.id}>
                      {col.nome}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => {
                  const melhor = Math.min(...l.valores.map((v) => v.custo))
                  return (
                    <tr key={l.criterio_id}>
                      <th scope="row">{legivel(l.criterio_id)}</th>
                      {dados.colunas.map((col) => {
                        const v = l.valores.find((x) => x.cenario_id === col.id)
                        const ganha = l.difere && v?.custo === melhor
                        return (
                          <td key={col.id} className={ganha ? 'comparar__melhor' : undefined}>
                            <span className="mono">{v?.ocorrencias ?? 0}</span>
                            <span className="comparar__custo"> ({v?.custo ?? 0})</span>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          <p className="field__hint">
            O número é quantas vezes o critério ocorreu; entre parênteses, o que
            ele custou. Custo negativo é recompensa — cinco dos critérios
            descrevem coisas que a escola QUER que aconteçam.
          </p>
        </>
      )}
    </>
  )
}

function Resumo({ colunas }: { colunas: Coluna[] }) {
  const menosPendentes = Math.min(...colunas.map((c) => c.aulas_pendentes))
  const melhorObjetivo = Math.min(...colunas.map((c) => c.valor_objetivo))
  const barata = maisBarata(colunas)

  return (
    <>
      <tr>
        <th scope="row">
          Custo mensal <span className="comparar__nota">— em reais, da grade montada</span>
        </th>
        {colunas.map((c) => (
          <td key={c.id} className={c.id === barata ? 'comparar__melhor' : undefined}>
            {c.custo_mes === null ? (
              <span className="field__hint">sem grade</span>
            ) : (
              <>
                <span className="mono">{reais(c.custo_mes)}</span>
                {(c.custo_aulas_pendentes ?? 0) > 0 && (
                  <span className="comparar__nota">
                    {' '}
                    — sem {c.custo_aulas_pendentes} aula(s) que ficaram de fora
                  </span>
                )}
              </>
            )}
          </td>
        ))}
      </tr>
      <tr>
        <th scope="row">Aulas sem lugar</th>
        {colunas.map((c) => (
          <td
            key={c.id}
            className={c.aulas_pendentes === menosPendentes ? 'comparar__melhor' : undefined}
          >
            <span className="mono">{c.aulas_pendentes}</span>
          </td>
        ))}
      </tr>
      <tr>
        <th scope="row">Aulas alocadas</th>
        {colunas.map((c) => (
          <td key={c.id}>
            <span className="mono">{c.aulas_alocadas}</span>
          </td>
        ))}
      </tr>
      <tr>
        <th scope="row">
          Nota total <span className="comparar__nota">— quanto menor, melhor</span>
        </th>
        {colunas.map((c) => (
          <td
            key={c.id}
            className={c.valor_objetivo === melhorObjetivo ? 'comparar__melhor' : undefined}
          >
            <span className="mono">{c.valor_objetivo}</span>
          </td>
        ))}
      </tr>
      <tr>
        <th scope="row">Diagnóstico</th>
        {colunas.map((c) => (
          <td key={c.id}>
            {c.conclusao === 'RESTRICAO'
              ? `é regra — ${c.relaxacoes} saída(s)`
              : c.conclusao === 'TEMPO'
                ? 'foi tempo'
                : c.conclusao === 'INDETERMINADO'
                  ? 'não provado'
                  : '—'}
          </td>
        ))}
      </tr>
      <tr>
        <th scope="row">Processado em</th>
        {colunas.map((c) => (
          <td key={c.id}>
            {c.processado_em
              ? new Date(c.processado_em).toLocaleString('pt-BR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })
              : 'nunca'}
          </td>
        ))}
      </tr>
    </>
  )
}
